import { motion } from "motion/react";
import { useLocation, useOutletContext } from "react-router-dom";
import { useState, useEffect } from "react";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { AnimatedDivider } from "../components/AnimatedDivider";
import { Helmet } from "react-helmet-async";

export function Contact() {
  const location = useLocation();
  const { hasLoaded, handleNavClick, isScrolledPop } = useOutletContext<{ hasLoaded: boolean, handleNavClick: (e: React.MouseEvent<HTMLAnchorElement>, href: string) => void, isScrolledPop?: boolean }>();
  const [isDocking, setIsDocking] = useState(() => Boolean(hasLoaded && location.state?.fromNav));
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  useEffect(() => {
    if (isDocking) {
      const timer = setTimeout(() => setIsDocking(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isDocking]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    
    const formData = new FormData(e.currentTarget);
    
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });
      
      if (response.ok) {
        setFormStatus("success");
      } else {
        setFormStatus("error");
      }
    } catch (error) {
      setFormStatus("error");
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact • Atharva Dhekne | tacitonic</title>
        <meta name="description" content="Get in touch with Atharva Dhekne (tacitonic) for stereo mastering, immersive audio, and consulting projects. Booking via Sterling Sound." />
        <meta name="robots" content="index, follow, max-image-preview:none" />
        <meta name="googlebot" content="index, follow, max-image-preview:none" />
        <link rel="canonical" href="https://tacitonic.com/contact" />
        <meta property="og:title" content="Contact • Atharva Dhekne | tacitonic" />
        <meta property="og:description" content="Get in touch with Atharva Dhekne (tacitonic) for stereo mastering, immersive audio, and consulting projects. Booking via Sterling Sound." />
        <meta property="og:url" content="https://tacitonic.com/contact" />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Atharva Dhekne (tacitonic)",
            "url": "https://tacitonic.com/contact",
            "mainEntity": {
              "@id": "https://tacitonic.com/#person"
            }
          })}
        </script>
      </Helmet>
      <main className="pt-40 pb-32 px-6 max-w-5xl mx-auto min-h-screen">
      <div className="mb-10 pb-4 relative">
        <motion.h1 
          layoutId={isScrolledPop ? undefined : `page-title-Contact`}
          initial={(!hasLoaded || isScrolledPop) ? { opacity: 0, scale: 0.95, y: 10 } : undefined}
          animate={{ opacity: 1, scale: 1, y: 0, color: "#FEFDF9" }}
          className="text-[31px] md:text-[52px] lg:text-[64px] font-bold tracking-tighter uppercase leading-none font-display w-fit origin-left text-white"
          transition={{ 
            duration: 1.2,
            ease: [0.16, 1, 0.3, 1],
            layout: { duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }
          }}
        >
          Contact
        </motion.h1>
        {!isDocking && (
          <div className="absolute bottom-0 left-0 w-full">
            <AnimatedDivider className="h-[3px] bg-white" />
          </div>
        )}
      </div>

      <section className="sr-only" aria-label="Booking and Contact Information">
        <h2>Book Mastering, Immersive Mixing, and Audio Engineering Sessions with Atharva Dhekne (tacitonic)</h2>
        <p>
          Connect with Atharva Dhekne (tacitonic) in New York City for stereo mastering, immersive mastering and spatial audio mixing (Dolby Atmos, Sony 360 Reality Audio, IAMF Eclipsa Audio), spectral audio restoration, music production, and music technology consulting. Studio booking and commercial label sessions are coordinated through Sterling Sound, with worldwide remote attendance available for artists and record labels across New York City, Los Angeles, Nashville, London, Mumbai, Pune, Delhi, Chennai, Bangalore, Kolkata, Tokyo, Seoul, Europe, Latin America, Asia, Africa, and Australia.
        </p>
      </section>

      <div className={`transition-opacity duration-700 delay-300 ${isDocking ? 'opacity-0' : 'opacity-100'}`}>
        <motion.div
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <div className="space-y-8">
            <p className="text-2xl md:text-3xl font-responsive-bold tracking-tight leading-relaxed opacity-90">
              For booking or project inquiries, please contact via <span data-nosnippet className="whitespace-nowrap"><a href="https://sterling-sound.com/" target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-100 transition-all hover-target group inline-flex items-center gap-1 align-bottom leading-none"><img src="https://media.tacitonic.com/media/Sterling-Sound-logo.svg?v=7" alt="Sterling Sound" width={171} height={34} className="h-[1.1em] md:h-[1.25em] w-auto object-contain sterling-logo-invert" /><ArrowUpRight size={20} className="text-white opacity-40 group-hover:opacity-100 transition-opacity" /></a>.</span>
              <br /><br />
              For general inquiries please fill out the contact form.
            </p>
            <p className="text-2xl md:text-3xl font-responsive-bold tracking-tight leading-relaxed opacity-90">
              @tacitonic everywhere on the internet.
            </p>
          </div>

          <form 
            className="space-y-6" 
            onSubmit={handleSubmit}
          >
            {/* Replace with actual Access Key from Web3Forms */}
            <input type="hidden" name="access_key" value="12ca9c1e-64fd-4d78-b422-dd601ff9ec0c" />
            
            <div className="space-y-2">
              <label htmlFor="name" className="font-sans text-xs uppercase opacity-60 tracking-widest font-bold">Name</label>
              <input 
                type="text" 
                id="name" 
                name="name"
                required
                disabled={formStatus !== "idle" && formStatus !== "error"}
                className="w-full bg-transparent border-b border-white/20 py-3 text-xl focus:outline-none focus:border-white transition-colors hover-target disabled:opacity-50"
                placeholder="Your Name"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="email" className="font-sans text-xs uppercase opacity-60 tracking-widest font-bold">Email</label>
              <input 
                type="email" 
                id="email" 
                name="email"
                required
                disabled={formStatus !== "idle" && formStatus !== "error"}
                className="w-full bg-transparent border-b border-white/20 py-3 text-xl focus:outline-none focus:border-white transition-colors hover-target disabled:opacity-50"
                placeholder="email@example.com"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="message" className="font-sans text-xs uppercase opacity-60 tracking-widest font-bold">Message</label>
              <textarea 
                id="message" 
                name="message"
                required
                rows={4}
                disabled={formStatus !== "idle" && formStatus !== "error"}
                className="w-full bg-transparent border-b border-white/20 py-3 text-xl focus:outline-none focus:border-white transition-colors hover-target resize-none disabled:opacity-50"
                placeholder="Describe your project"
              ></textarea>
            </div>

            {/* Honeypot Spam Protection */}
            <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />
            
            {formStatus === "success" ? (
              <div className="flex items-center justify-center gap-3 w-full py-4 border-2 border-[#9af104] text-[#9af104] mt-8 font-display uppercase tracking-tight font-bold">
                <CheckCircle2 size={24} />
                <span>Message Sent</span>
              </div>
            ) : formStatus === "error" ? (
              <button 
                type="submit"
                className="w-full py-4 border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-colors duration-300 text-[18px] font-bold uppercase tracking-tight mt-8 font-display"
              >
                Error. Try Again
              </button>
            ) : (
              <button 
                type="submit"
                disabled={formStatus === "submitting"}
                className="w-full py-4 border-2 border-white/20 hover:bg-white hover:text-black transition-colors duration-300 text-[18px] font-bold uppercase tracking-tight hover-target mt-8 font-display disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-white"
              >
                {formStatus === "submitting" ? "Sending..." : "Send Message"}
              </button>
            )}
            
          </form>
        </div>
      </motion.div>
      </div>
    </main>
    </>
  );
}
